import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import DropZone from "../components/DropZone";

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

let _tuid = 0;
const withKey = (o) => ({ ...o, _k: o._k ?? `t${++_tuid}` });
const stripKeys = (arr) => arr.map(({ _k, ...rest }) => rest);

export default function TestimonialsEditor() {
  const [items, setItems] = useState([]);
  const [speed, setSpeed] = useState(60);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/kv/testimonials").then(({ data }) => Array.isArray(data) && setItems(data.map(withKey)));
    api.get("/content/kv/branding").then(({ data }) => data?.testimonials_speed_seconds && setSpeed(Number(data.testimonials_speed_seconds)));
  }, []);

  const upd = (i, k, v) => setItems((arr) => { const a = [...arr]; a[i] = { ...a[i], [k]: v }; return a; });
  const add = () => setItems((arr) => [...arr, withKey({ author: "", role: "", company: "", photo: "", text: "" })]);
  const remove = (i) => setItems((arr) => arr.filter((_, j) => j !== i));
  const move = (i, dir) => setItems((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/kv/testimonials", { value: stripKeys(items) });
      const { data: branding } = await api.get("/content/kv/branding");
      await api.put("/content/kv/branding", { value: { ...(branding || {}), testimonials_speed_seconds: speed } });
      toast.success("Depoimentos salvos.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">Depoimentos</h1>
          <p className="text-sm text-neutral-500 mt-1">Faixa rolante na Home, antes do CTA final.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="testimonials-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}</button>
      </div>

      <section className="border border-black/10 p-5 rounded mb-6">
        <label className="text-xs text-neutral-500 flex justify-between">
          <span>Velocidade da rolagem (segundos por loop completo)</span>
          <span className="font-mono">{speed}s</span>
        </label>
        <input type="range" min={20} max={120} step={2} value={speed} onChange={(e) => setSpeed(Number(e.target.value))} data-testid="testimonials-speed" className="w-full" />
        <div className="text-[10px] text-neutral-400 flex justify-between"><span>Mais rápido (20s)</span><span>Mais lento (120s)</span></div>
      </section>

      <section className="border border-black/10 p-5 rounded">
        <div className="flex justify-between items-center mb-4">
          <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Cards de depoimento ({items.length})</div>
          <button onClick={add} className="text-xs bg-accent-yellow px-3 py-1 rounded-full font-semibold" data-testid="testimonial-add"><Plus className="w-3 h-3 inline" /> Adicionar</button>
        </div>
        <div className="space-y-4">
          {items.map((t, i) => (
            <div key={t._k || `idx-${i}`} className="border border-black/10 p-4 rounded" data-testid={`testimonial-${i}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 pt-1">
                  <button onClick={() => move(i, -1)} title="Subir" className="text-neutral-400 hover:text-black"><GripVertical className="w-3 h-3" /></button>
                  <span className="text-[10px] font-mono text-neutral-400">{i + 1}</span>
                </div>
                <div className="flex-1 grid md:grid-cols-3 gap-3">
                  <input value={t.author || ""} onChange={(e) => upd(i, "author", e.target.value)} placeholder="Nome do autor" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
                  <input value={t.role || ""} onChange={(e) => upd(i, "role", e.target.value)} placeholder="Cargo" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
                  <input value={t.company || ""} onChange={(e) => upd(i, "company", e.target.value)} placeholder="Empresa" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
                  <textarea value={t.text || ""} onChange={(e) => upd(i, "text", e.target.value)} placeholder="Depoimento (frase curta)" rows={2} className="border border-black/15 px-2 py-1.5 rounded text-sm md:col-span-3" />
                  <div className="md:col-span-2">
                    <label className="text-[10px] text-neutral-500">Foto (opcional)</label>
                    {t.photo && <img src={resolveSrc(t.photo)} alt="" className="w-12 h-12 rounded-full object-cover my-1" />}
                    <DropZone multiple={false} onUploaded={(f) => upd(i, "photo", f.url)} />
                    <input value={t.photo || ""} onChange={(e) => upd(i, "photo", e.target.value)} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-1" />
                  </div>
                </div>
                <button onClick={() => remove(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {!items.length && <div className="text-xs text-neutral-500 text-center py-6">Nenhum depoimento ainda. Clique em &ldquo;Adicionar&rdquo;.</div>}
        </div>
      </section>
    </div>
  );
}
